import { useQuery } from '@tanstack/react-query';

import { isDesktop } from '../../app/desktop';
import { api } from '../../lib/api';

/**
 * The desktop app's current release, and whether to offer it here at all.
 *
 * ## Why the version is not in the frontend
 *
 * Because the installer's filename carries it - `GetVibed-1.0.0-x64.exe` - so a
 * hard-coded link is wrong the moment somebody publishes 1.0.1, and wrong in
 * the worst way: a button that still looks right and downloads a 404. The
 * server reads the same manifest the auto-updater polls, so the download and
 * the updater cannot disagree about what the current version is.
 *
 * ## It does not run in the desktop app
 *
 * An app advertising itself to itself is the kind of detail that makes software
 * feel unmaintained. `enabled` is false in the shell, so there is no request
 * and no button - not a button that is fetched and then hidden.
 */
export interface DesktopBuild {
  label: string;
  url: string;
  /** Bytes. Zero when the manifest did not say, which renders as no size. */
  size: number;
}

export interface DesktopRelease {
  available: boolean;
  version: string | null;
  platforms: Partial<Record<'win' | 'mac' | 'linux', DesktopBuild>>;
}

/**
 * Which build this visitor should be offered.
 *
 * `userAgentData.platform` is the modern answer and is missing in Safari and
 * Firefox, so the user-agent string stays as the fallback. Neither is reliable
 * enough to hide the other platforms over - somebody on a Mac choosing the
 * Windows build is a support question, not a broken page - so this only decides
 * what goes on the button, and the rest stay reachable beside it.
 */
export function guessPlatform(): 'win' | 'mac' | 'linux' | null {
  if (typeof navigator === 'undefined') return null;

  const hint = (
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.userAgent
  ).toLowerCase();

  if (hint.includes('win')) return 'win';
  if (hint.includes('mac') || hint.includes('darwin')) return 'mac';
  if (hint.includes('linux') || hint.includes('x11')) return 'linux';

  return null;
}

/** Bytes as something worth putting next to a download. */
export function megabytes(bytes: number): string | null {
  return bytes > 0 ? `${Math.round(bytes / 1048576)} MB` : null;
}

export function useDesktopRelease() {
  const { data } = useQuery<DesktopRelease>({
    queryKey: ['desktop-release'],
    queryFn: () => api.get<DesktopRelease>('/desktop/latest'),
    enabled: !isDesktop,

    /*
     * A release changes a few times a year. Refetching it on every window focus
     * - the default - would be a request per tab switch for a number that is
     * the same every time.
     */
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,

    /*
     * One attempt. Nothing here is important enough to retry: if it fails the
     * button does not appear, which is the same as the state before any release
     * was published, and the page is entirely fine without it.
     */
    retry: false,
  });

  return data?.available ? data : null;
}
